import React, { useEffect, useRef, useState } from "react";
import { Message } from "../../lib/messagesLib";
import MessageRow from "./messageRow";

interface MessagesCardProps {
  messages: Message[];
  onRuleCreated?: () => Promise<void>;
  showSnackbar?: (message: string, type: "success" | "error" | "info") => void;
  currentPage?: number;
  totalPages?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
}

interface MessageState {
  message: Message;
  status: "entering" | "stable" | "exiting";
}

const MessagesCard: React.FC<MessagesCardProps> = ({
  messages,
  onRuleCreated,
  showSnackbar,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
}) => {
  const [displayMessages, setDisplayMessages] = useState<MessageState[]>([]);
  const previousMessagesRef = useRef<Message[]>([]);
  const isInitialMount = useRef(true);
  // Track if component has been mounted at least once to prevent reset
  const hasMountedRef = useRef(false);
  // Track previous page to detect page changes
  const previousPageRef = useRef<number | undefined>(currentPage);

  useEffect(() => {
    console.log("[MessagesCard] Effect triggered", {
      messagesLength: messages.length,
      displayMessagesLength: displayMessages.length,
      isInitialMount: isInitialMount.current,
      previousMessagesLength: previousMessagesRef.current.length,
      currentPage,
      totalPages,
    });

    // On initial mount, just show all messages as stable
    if (isInitialMount.current && messages.length > 0) {
      console.log("[MessagesCard] Initial mount, setting messages as stable", {
        count: messages.length,
      });
      setDisplayMessages(
        messages.map((message) => ({ message, status: "stable" as const }))
      );
      previousMessagesRef.current = messages;
      isInitialMount.current = false;
      hasMountedRef.current = true;
      return;
    }

    // If messages array is empty AND we've mounted before, clear display
    // But if it's initial mount and empty, don't clear (might be loading)
    if (messages.length === 0) {
      if (hasMountedRef.current) {
        console.log(
          "[MessagesCard] Messages array is empty after mount, clearing display"
        );
        setDisplayMessages([]);
        previousMessagesRef.current = [];
      } else {
        console.log(
          "[MessagesCard] Messages array is empty on initial mount, waiting"
        );
      }
      return;
    }

    // Mark as mounted if we get here with messages
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
    }

    const previousMessages = previousMessagesRef.current;

    // Check if page changed - if so, treat all messages as new
    const pageChanged =
      previousPageRef.current !== undefined &&
      previousPageRef.current !== currentPage;

    console.log("[MessagesCard] Comparing messages", {
      previousCount: previousMessages.length,
      currentCount: messages.length,
      previousPage: previousPageRef.current,
      currentPage,
      pageChanged,
    });

    // If no previous messages, just set all as stable
    if (previousMessages.length === 0) {
      console.log("[MessagesCard] No previous messages, setting as stable", {
        count: messages.length,
      });
      setDisplayMessages(
        messages.map((message) => ({ message, status: "stable" as const }))
      );
      previousMessagesRef.current = messages;
      previousPageRef.current = currentPage;
      return;
    }

    // If page changed, treat all messages as new (different page = different messages)
    if (pageChanged) {
      console.log("[MessagesCard] Page changed, treating all messages as new", {
        previousPage: previousPageRef.current,
        currentPage,
        count: messages.length,
      });
      setDisplayMessages(
        messages.map((message) => ({ message, status: "stable" as const }))
      );
      previousMessagesRef.current = messages;
      previousPageRef.current = currentPage;
      return;
    }

    const previousIds = new Set(previousMessages.map((m) => m.id));
    const currentIds = new Set(messages.map((m) => m.id));

    // Find new, stable, and exiting messages
    const newMessages = messages.filter((m) => !previousIds.has(m.id));
    const exitingMessages = previousMessages.filter(
      (m) => !currentIds.has(m.id)
    );

    console.log("[MessagesCard] Message comparison", {
      previousCount: previousMessages.length,
      currentCount: messages.length,
      newCount: newMessages.length,
      exitingCount: exitingMessages.length,
      allNew: newMessages.length === messages.length,
      pageChanged,
    });

    // If all messages are new (e.g., page change), immediately show them as stable
    // This avoids animation issues when the entire list is replaced
    if (newMessages.length === messages.length && messages.length > 0) {
      console.log(
        "[MessagesCard] All messages are new (page change), showing immediately",
        {
          count: messages.length,
        }
      );
      setDisplayMessages(
        messages.map((message) => ({ message, status: "stable" as const }))
      );
      previousMessagesRef.current = messages;
      previousPageRef.current = currentPage;
      return;
    }

    // Build the transition state with the proper order
    const transitionStates: MessageState[] = [];

    // Add new messages at the top (marked as entering)
    newMessages.forEach((message) => {
      transitionStates.push({ message, status: "entering" });
    });

    // Add stable messages (that exist in both lists)
    messages
      .filter((m) => previousIds.has(m.id))
      .forEach((message) => {
        transitionStates.push({ message, status: "stable" });
      });

    // Add exiting messages at the bottom
    exitingMessages.forEach((message) => {
      transitionStates.push({ message, status: "exiting" });
    });

    console.log("[MessagesCard] Using transition states", {
      transitionStatesCount: transitionStates.length,
    });
    setDisplayMessages(transitionStates);

    // After animation completes, remove exiting messages and mark entering as stable
    const timer = setTimeout(() => {
      console.log("[MessagesCard] Transition complete, setting stable", {
        count: messages.length,
      });
      setDisplayMessages(
        messages.map((message) => ({ message, status: "stable" }))
      );
      previousMessagesRef.current = messages;
      previousPageRef.current = currentPage;
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, currentPage, totalPages]);

  console.log("[MessagesCard] Render", {
    messagesLength: messages.length,
    displayMessagesLength: displayMessages.length,
    currentPage,
    totalPages,
    totalCount,
  });

  return (
    <div className="order-3 lg:order-none">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent Emails</h2>
          {/* Pagination - only show if props are provided */}
          {totalPages !== undefined &&
            totalPages > 1 &&
            currentPage !== undefined &&
            onPageChange && (
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i).map((page) => {
                  // Show first page, last page, current page, and pages around current
                  const showPage =
                    page === 0 ||
                    page === totalPages - 1 ||
                    (page >= currentPage - 1 && page <= currentPage + 1);

                  if (!showPage) {
                    // Show ellipsis
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span
                          key={page}
                          className="px-2 text-gray-500 dark:text-gray-400"
                        >
                          ...
                        </span>
                      );
                    }
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => onPageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        page === currentPage
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
                      }`}
                    >
                      {page + 1}
                    </button>
                  );
                })}
                {totalCount !== undefined && (
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    ({totalCount} total)
                  </span>
                )}
              </div>
            )}
        </div>
        {displayMessages.length > 0 ? (
          <div className="space-y-2 relative">
            {displayMessages.map(({ message, status }) => (
              <div
                key={message.id}
                className="transition-all duration-500 ease-in-out"
                style={{
                  opacity:
                    status === "entering" || status === "exiting" ? 0 : 1,
                  transform:
                    status === "entering"
                      ? "translateY(-16px)"
                      : status === "exiting"
                        ? "translateY(8px)"
                        : "translateY(0)",
                  transition:
                    "opacity 500ms ease-in-out, transform 500ms ease-in-out",
                }}
              >
                <MessageRow
                  message={message}
                  onRuleCreated={onRuleCreated}
                  showSnackbar={showSnackbar}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center p-4 bg-gray-100 dark:bg-gray-700 rounded">
            No recent emails found.
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesCard;
